/**
 * Home — Create Templates & Macros
 *
 * Template / header / footer / macro editors, list tables, catalog sidebar.
 * Auth: CRE (hospital sign-in) or Radical (embed token). Catalog fetch uses a CRE-specific catalog type when needed.
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import debounce from "lodash.debounce";
import "react-loading-skeleton/dist/skeleton.css";
import Card from "./Card";
import RTE from "../RTE";
import { Link, useLocation } from "react-router-dom";
import Footer from "../footer/Footer";
import Sidebar from "./Sidebar";
import TableSection from "./Tablesection";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  createTemplate,
  updateTemplate,
  getSingleTemplate,
  getClientHeader,
  getClientFooter,
  isTemplateExists,
  useMacros,
  useTemplates,
  deleteMacro,
  deactivateTemplate,
} from "../../utils/API_SERVICE";
import { useAuth } from "../../Hooks/useAuth";
import dots from "../../assets/icons/dots.png";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCatalog } from "../../api/hooks/useCatalog";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import BackButton from "../common/BackButton";
import useHomeRteWithCursor from "../../Hooks/useHomeRteWithCursor";
import DarkModeToggle from "../darkmodetoogle/DarkModeToggle";
import LanguageDropdown from "../common/LanguageDropdown";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../i18n/hooks/useI18nLanguage";
import { UI_LANGUAGES } from "../../constants/languages";
import { resolveRelatedUserId } from "../../utils/relatedUserId";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { getSelectedCreOrganisationId } from "../../cre/creOrganisation";
import {
  CRE_PERMISSION,
  hasCrePermission,
} from "../../cre/crePermissions";

// -----------------------------------------------------------------------------
// Sortable wrapper for drag-and-drop (DnD) sections
// -----------------------------------------------------------------------------
const SortableItem = React.memo((props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  if (props.children) {
    return (
      <div ref={setNodeRef} style={style} {...attributes}>
        {props.children(listeners)}
      </div>
    );
  }
  return null;
});

// -----------------------------------------------------------------------------
// Home — Create Templates & Macros
// -----------------------------------------------------------------------------
function Home() {
  const location = useLocation();
  const accessToken = useAuth();
  const { isCreAuth, creUser, radicalUser } =
    usePatientAuthFlags();
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const creOrganisationId = isCreAuth
    ? getSelectedCreOrganisationId(creUser, creSelectedOrganisationId)
    : null;
  // CRE: isAdmin OR MANAGE_DEFAULT_TEMPLATES (Default + Load DSR Data).
  // Radical: admin only (unchanged).
  const canManageDefaultTemplates = isCreAuth
    ? hasCrePermission(creUser, CRE_PERMISSION.MANAGE_DEFAULT_TEMPLATES)
    : !!radicalUser?.isAdmin;
  // Radical only: restrict create/edit to ADMIN; CRE unchanged (all can create/edit)
  const canCreateOrEditTemplates =
    isCreAuth ? true : !!radicalUser?.isAdmin;
  const { t } = useTranslation("common");
  const { language: currentLanguage, changeLanguage } = useI18nLanguage();

  // --- Component state (templates, macros, catalog, editor content) ---
  const [state, setState] = useState({
    loading: true,
    selectedCard: 1,
    name: "",
    items: ["editor", "sidebar"],
    templateSettings: {
      isDefault: false,
      loadDsrData: false,
    },
    draggingEnabled: true,
    isEditing: false,
    isMacroEditing: false,
    selectedMacroId: null,
    headerContent: "",
    footerContent: "",
    macroContent: "",
    templateData: [],
    macrosData: [],
    templateId: null,
    catalogTableName: [],
    catalogTableItems: [],
    clientHeaderContent: "",
    headerTableItems: [],
    clientFooterContent: "",
    footerTableItems: [],
    templateContent: "",
    templateInstructions: "",
    language: currentLanguage,
    anathomies: [],
    anathomy: "",
  });

  // --- Refs ---
  const hasFetchedCatalogsRef = useRef(false);

  // --- State helpers ---
  const updateState = useCallback((updates) => {
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  }, []);

  // Memoize setName to prevent infinite loops in Sidebar
  const handleSetName = useCallback(
    (value) => {
      updateState({ name: value });
    },
    [updateState],
  );

  /** Sync UI language from hook into state */
  useEffect(() => {
    if (state.language !== currentLanguage) {
      updateState({ language: currentLanguage });
    }
  }, [currentLanguage, state.language, updateState]);

  const updateTemplateSettings = useCallback((key, value) => {
    setState((prevState) => ({
      ...prevState,
      templateSettings: {
        ...prevState.templateSettings,
        [key]: value,
      },
    }));
  }, []);

  /** Debounced content updates for macro / header / footer to limit API or re-renders */
  const debouncedSetMacro = useMemo(
    () => debounce((value) => updateState({ macroContent: value }), 300),
    [updateState],
  );

  const debouncedSetClientHeader = useMemo(
    () => debounce((value) => updateState({ headerContent: value }), 300),
    [updateState],
  );

  const debouncedSetClientFooter = useMemo(
    () => debounce((value) => updateState({ footerContent: value }), 300),
    [updateState],
  );

  /** Build macro payload for create/update from current editor state */
  const generateMacroData = () => {
    const formattedMacroContent = state.macroContent
      ? state.macroContent.replace(/class="([^"]*)"/g, "class='$1'")
      : "";

    return {
      macroId: state.selectedMacroId,
      macroName: state.name,
      content: formattedMacroContent,
    };
  };

  const relatedUserId = useMemo(
    () =>
      resolveRelatedUserId({
        search: location.search,
        creUserId: creUser?.userId,
        radicalUserId: radicalUser?.userId,
      }),
    [location.search, creUser?.userId, radicalUser?.userId],
  );

  const {
    data: templatesPayload,
    isLoading: isTemplatesLoading,
    refetch: refetchTemplatesQuery,
  } = useTemplates({
    // Radical: templates are English only for now.
    language: isCreAuth ? currentLanguage || null : "en",
    ...(relatedUserId ? { relatedUserId } : {}),
    ...(creOrganisationId ? { OrganisationId: creOrganisationId } : {}),
  });

  const {
    data: macrosPayload,
    isLoading: isMacrosLoading,
    refetch: refetchMacrosQuery,
  } = useMacros({
    ...(relatedUserId ? { relatedUserId } : {}),
  });

  const templateRows = useMemo(() => {
    const templatesArray = Array.isArray(templatesPayload) ? templatesPayload : [];

    return templatesArray.map((template) => {
      const userId = template.createdBy || template.CreatedBy || "N/A";

      return {
        userId,
        template: template.templateName,
        templateId: template.templateId,
        isDefault: !!(template.isDefault ?? template.IsDefault),
        language: template.language || "",
      };
    });
  }, [templatesPayload]);

  useEffect(() => {
    updateState({
      loading: !!(isTemplatesLoading || isMacrosLoading),
      templateData: templateRows,
      macrosData: Array.isArray(macrosPayload) ? macrosPayload : [],
    });
  }, [
    isTemplatesLoading,
    isMacrosLoading,
    templateRows,
    macrosPayload,
    updateState,
  ]);

  const { getCatalog, getAnatomies } = useCatalog();

  // --- Effects: run once on mount when accessToken is available ---
  /** Load catalog (species, modality, study types, users, anatomies) and table catalog. */
  useEffect(() => {
    if (!accessToken) return;
    if (hasFetchedCatalogsRef.current) return;
    hasFetchedCatalogsRef.current = true;

    const fetchCatalogs = async () => {
      try {
        const tableCatalog = !isCreAuth
          ? await getCatalog({ catalogType: 4 })
          : await getCatalog({ catalogType: 9 });
        // Use new anatomies endpoint
        const anathomyCatalog = await getAnatomies();

        if (tableCatalog) {
          const rawItems = Array.isArray(tableCatalog.catalogItems)
            ? tableCatalog.catalogItems
            : [];
          const items = rawItems
            .filter(
              (item) =>
                item.table_name != null &&
                item.table_name !== "" &&
                item.code != null,
            )
            .map((item) => ({
              tableName: item.table_name,
              table_code: item.code,
            }));
          // Dropdown must use same table names as items so filter works (API type 9: catalogItemGroups has "PATIENT DETAILS" but items have table_name "ANATHOMIES")
          const namesFromItems = [...new Set(items.map((i) => i.tableName))]
            .filter(Boolean)
            .sort();
          const namesFromGroups = Array.isArray(tableCatalog.catalogItemGroups)
            ? tableCatalog.catalogItemGroups
                .map((item) => item.table_name)
                .filter(Boolean)
            : [];
          const catalogTableName =
            namesFromItems.length > 0 ? namesFromItems : namesFromGroups;
          updateState({
            catalogTableItems: items,
            catalogTableName,
          });
        }

        if (anathomyCatalog) {
          updateState({
            anathomies: anathomyCatalog.catalogItems.map((item) => ({
              id: item.id,
              code: item.code,
              name: item.name,
            })),
          });
        }
      } catch (error) {
        console.error("Error fetching catalog data:", error);
      }
    };

    fetchCatalogs();
  }, [
    accessToken,
    getCatalog,
    getAnatomies,
    updateState,
    isCreAuth,
  ]);

  // --- Client header / footer (for Template Header & Footer cards) ---
  const fetchClientHeader = async () => {
    try {
      const response = await getClientHeader({});
      if (response.success) {
        updateState({
          headerTableItems: response.payload,
          clientHeaderContent: response.payload.content,
        });
      } else {
        toast.error("Failed to fetch client header");
      }
    } catch (error) {
      toast.error("Error fetching client header");
    }
  };

  const fetchClientFooter = async () => {
    try {
      const response = await getClientFooter({});
      if (response.success) {
        updateState({
          footerTableItems: response.payload,
          clientFooterContent: response.payload.content,
        });
      } else {
        toast.error("Failed to fetch client footer");
      }
    } catch (error) {
      toast.error("Error fetching client footer");
    }
  };

  // --- Template & macro actions (save, delete, deactivate, edit) ---
  const saveTemplate = async () => {
    const formattedBodyContent = state.templateContent
      ? state.templateContent.replace(/class="([^"]*)"/g, "class='$1'")
      : "";

    try {
      // Check if template name already exists before creating
      const templateName = state.name || "Default Template Name";
      const templateExistsResponse = await isTemplateExists(templateName);

      if (
        templateExistsResponse.success &&
        templateExistsResponse.payload.isExists
      ) {
        toast.error(`Template with name "${templateName}" already exists.`);
        return;
      }

      const templateData = {
        TemplateName: templateName,
        Content: formattedBodyContent || "",
        SpeciesIds: [],
        ModalityTypesIds: [],
        StudyTypesIds: [],
        Description: "",
        IsActive: true,
        Header: "",
        Footer: "",
        TemplateInstructions: state.templateInstructions || "",
        Anathomy: state.anathomy || "",
        IsDefault: !!state.templateSettings.isDefault,
        loadDsrData: !!state.templateSettings.loadDsrData,
        Language: currentLanguage || state.language || null,
      };

      const templateResponse = await createTemplate(templateData);
      if (templateResponse?.payload?.templateId) {
        const newTemplateId = templateResponse.payload.templateId;
        updateState({ templateId: newTemplateId });
      }

      toast.success("Template saved successfully!");
      await refetchTemplatesQuery();

      updateState({ name: "" });
      updateState({ templateContent: "" });
      updateState({ templateInstructions: "" });
      updateState({ anathomy: "" });
      updateState({
        templateSettings: {
          isDefault: false,
          loadDsrData: false,
        },
      });
    } catch (error) {
      console.error("Failed to save template or macro", error);
      toast.error("Failed to save template or macro");
    }
  };

  const handleDeleteMacro = async (macro) => {
    try {
      await deleteMacro(macro.macroId);
      await refetchMacrosQuery();
      toast.success("Macro deleted successfully!");
    } catch (error) {
      console.error("Error deleting macro:", error);
      toast.error("Failed to delete macro");
    }
  };

  const handleDeactivateTemplate = async (template) => {
    if (template?.isDefault && !canManageDefaultTemplates) {
      toast.warning("Only admin users can deactivate default templates.");
      return;
    }
    try {
      await deactivateTemplate(template.templateId);
      await refetchTemplatesQuery();
      toast.success("Template deactivated successfully!");
    } catch (error) {
      console.error("Error deactivating template:", error);
      toast.error("Failed to deactivate template");
    }
  };

  /** Toggle card (Macro, Template, Header, Footer); also used after saving a new macro (clears macro + template draft fields). */
  const resetEditorDraftState = useCallback(() => {
    debouncedSetMacro.cancel?.();
    setState((prev) => ({
      ...prev,
      name: "",
      isEditing: false,
      isMacroEditing: false,
      selectedMacroId: null,
      templateId: null,
      macroContent: "",
      templateContent: "",
      templateInstructions: "",
      anathomy: "",
      templateSettings: { isDefault: false, loadDsrData: false },
    }));
  }, [debouncedSetMacro]);

  const handleCardClick = (index) => {
    if (state.selectedCard === index) {
      resetEditorDraftState();
      updateState({
        selectedCard: null,
      });
    } else {
      resetEditorDraftState();
      updateState({
        selectedCard: index,
      });

      if (index === 2) {
        updateState({ name: "Template Header" });
        fetchClientHeader();
      } else if (index === 3) {
        updateState({ name: "Template Footer" });
        fetchClientFooter();
      }
    }
  };

  const handleRadioChange = (index) => {
    resetEditorDraftState();
    updateState({ selectedCard: index });
  };

  /** Load single template into editor for editing (header, footer, species, macros, etc.) */
  const handleEditClick = async (row) => {
    if (row?.isDefault && !canManageDefaultTemplates) {
      toast.warning("Only admin users can edit default templates.");
      return;
    }
    try {
      // Always switch to Template Content card when editing a template
      updateState({
        selectedCard: 1,
      });

      const fetchedTemplate = await getSingleTemplate({ templateId: row.templateId });
      if (fetchedTemplate && fetchedTemplate.payload) {
        const template = fetchedTemplate.payload;
        const templateContent = fetchedTemplate.payload.content;
        updateState({ templateContent: templateContent });
        updateState({
          templateId: template.templateId,
          name: template.templateName,
          headerContent: template.header || "",
          footerContent: template.footer || "",
          templateInstructions: template.templateInstructions || "",
          anathomy: template.anathomy || "",
          templateSettings: {
            isDefault: !!template.isDefault,
            loadDsrData: !!(
              template.loadDsrData ?? template.LoadDsrData ?? false
            ),
          },
          isEditing: true,
        });
        if (template.language && template.language !== currentLanguage) {
          changeLanguage(template.language);
        }
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    }
  };

  /** Load macro into editor for editing */
  const handleEditMacro = (macro) => {
    updateState({
      isMacroEditing: true,
      selectedMacroId: macro.macroId,
      name: macro.macroName,
      macroContent: macro.content,
    });
  };

  /** Persist changes to an existing template (update API) */
  const updateExistingTemplate = async () => {
    const formattedBodyContent = state.templateContent
      ? state.templateContent.replace(/class="([^"]*)"/g, "class='$1'")
      : "";

    try {
      const templateData = {
        TemplateId: state.templateId,
        TemplateName: state.name,
        Content: formattedBodyContent,
        SpeciesIds: [],
        ModalityTypesIds: [],
        StudyTypesIds: [],
        Description: "",
        IsActive: true,
        Header: state.headerContent,
        Footer: state.footerContent,
        TemplateInstructions: state.templateInstructions || "",
        Anathomy: state.anathomy || "",
        IsDefault: !!state.templateSettings.isDefault,
        loadDsrData: !!state.templateSettings.loadDsrData,
        Language: currentLanguage || state.language || null,
      };

      await updateTemplate(templateData);
      toast.success("Template updated successfully!");

      updateState({
        isEditing: false,
        name: "",
        templateContent: "",
        templateInstructions: "",
        anathomy: "",
        templateSettings: {
          isDefault: false,
          loadDsrData: false,
        },
        headerContent: "",
        footerContent: "",
        templateId: null,
      });

      await refetchTemplatesQuery();
    } catch (error) {
      console.error("Failed to update template:", error);
      toast.error(error.message || "Failed to update template");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      updateState({
        items: arrayMove(
          state.items,
          state.items.indexOf(active.id),
          state.items.indexOf(over.id),
        ),
      });
    }
  };

  const handleFocus = () => updateState({ draggingEnabled: false });
  const handleBlur = () => updateState({ draggingEnabled: true });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: state.draggingEnabled ? 5 : Infinity,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const macroEditorRef = useRef();
  const templateEditorRef = useRef();
  const headerEditorRef = useRef();
  const footerEditorRef = useRef();

  // Use the hook for cursor logic only for macro and template
  const { addRecordedTranscript: addMacroAtCursor } = useHomeRteWithCursor({
    editorRef: macroEditorRef,
    value: state.macroContent,
    setValue: (val) => updateState({ macroContent: val }),
  });
  const { addRecordedTranscript: addTemplateAtCursor } = useHomeRteWithCursor({
    editorRef: templateEditorRef,
    value: state.templateContent,
    setValue: (val) => updateState({ templateContent: val }),
  });
  const { addRecordedTranscript: addHeaderAtCursor } = useHomeRteWithCursor({
    editorRef: headerEditorRef,
    value: state.clientHeaderContent,
    setValue: (val) => updateState({ clientHeaderContent: val }),
  });
  const { addRecordedTranscript: addFooterAtCursor } = useHomeRteWithCursor({
    editorRef: footerEditorRef,
    value: state.clientFooterContent,
    setValue: (val) => updateState({ clientFooterContent: val }),
  });

  const onDoubleClickItem = (text) => {
    if (state.selectedCard === 0) {
      addMacroAtCursor(text);
    } else if (state.selectedCard === 1) {
      addTemplateAtCursor(text);
    } else if (state.selectedCard === 2) {
      addHeaderAtCursor(text);
    } else if (state.selectedCard === 3) {
      addFooterAtCursor(text);
    }
  };

  return (
    <div className="overflow-hidden z-50 p-2">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto items-start sm:items-center">
          <Link
            to={`/patient/Study/getStudyById${location.search}`}
            className="w-full sm:w-auto"
          >
            <BackButton text={t("createReport")} />
          </Link>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
            {t("createTemplate")}
          </h1>
        </div>
        <div className="flex items-center gap-2 px-2 py-1 w-full sm:w-auto justify-end sm:justify-start">
          <LanguageDropdown
            value={currentLanguage}
            onChange={changeLanguage}
            languages={UI_LANGUAGES}
          />
          <DarkModeToggle />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 mt-4 gap-3">
        <div onClick={() => handleCardClick(0)}>
          <Card
            heading={t("macro")}
            paragraph="This is a sample paragraph."
            className=""
            radioValue={state.selectedCard === 0}
            onRadioChange={() => handleRadioChange(0)}
          />
        </div>
        <div onClick={() => handleCardClick(1)}>
          <Card
            heading={t("templateContent")}
            paragraph="Another description for this card."
            className=""
            radioValue={state.selectedCard === 1}
            onRadioChange={() => handleRadioChange(1)}
          />
        </div>
        <div onClick={() => handleCardClick(2)}>
          <Card
            heading={t("templateHeader")}
            paragraph="Another description for this card."
            className=""
            radioValue={state.selectedCard === 2}
            onRadioChange={() => handleRadioChange(2)}
          />
        </div>
        <div onClick={() => handleCardClick(3)}>
          <Card
            heading={t("templateFooter")}
            paragraph="Another description for this card."
            className=""
            radioValue={state.selectedCard === 3}
            onRadioChange={() => handleRadioChange(3)}
          />
        </div>
      </div>
      <TableSection
        tableData={state.templateData}
        macrosData={state.macrosData}
        handleEditClick={handleEditClick}
        handleEditMacro={handleEditMacro}
        handleCardClick={handleCardClick}
        handleDeleteClick={
          state.selectedCard === 0
            ? handleDeleteMacro
            : handleDeactivateTemplate
        }
        loading={state.loading}
        canManageDefaultTemplates={canManageDefaultTemplates}
        canCreateOrEditTemplates={canCreateOrEditTemplates}
        displayHeaderItems={state.selectedCard === 2}
        headerTableItems={state.headerTableItems}
        displayFooterItems={state.selectedCard === 3}
        footerTableItems={state.footerTableItems}
        hideTemplateFields={state.selectedCard === 0}
        selectedCard={state.selectedCard}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={state.items}
          strategy={horizontalListSortingStrategy}
        >
          <div className="flex flex-col lg:flex-row mt-6 gap-1">
            {state.items.map((id) => {
              if (id === "editor") {
                return (
                  <div className="lg:w-4/6" key={id}>
                    <SortableItem id={id}>
                      {(listeners) => (
                        <div className="">
                          {state.selectedCard === 0 && (
                            <div className="relative bg-white pt-12 rounded-2xl dark:bg-gray-800">
                              <div
                                className="drag-handle bg-[#CBEA7B] hover:bg-[#A4C639] dark:bg-[#A4C639] dark:hover:bg-[#CBEA7B] w-8 h-8 rounded-full flex justify-center items-center absolute z-50 right-0 top-0 mt-2 mr-1"
                                {...listeners}
                              >
                                <img src={dots} alt="" className="w-1 h-4" />
                              </div>
                              <div>
                                <RTE
                                  name="editor"
                                  heightValue={400}
                                  value={state.macroContent}
                                  defaultValue={state.macroContent}
                                  editorRef={macroEditorRef}
                                  onFocus={handleFocus}
                                  onBlur={handleBlur}
                                  onChange={(value) => debouncedSetMacro(value)}
                                  language={state.language}
                                />
                              </div>
                            </div>
                          )}
                          {state.selectedCard === 1 && (
                            <div className="relative bg-white pt-12 rounded-2xl dark:bg-gray-800">
                              <div
                                className="drag-handle bg-[#CBEA7B] hover:bg-[#A4C639] dark:bg-[#A4C639] dark:hover:bg-[#CBEA7B] w-8 h-8 rounded-full flex justify-center items-center absolute z-50 right-0 top-0 mt-1 mr-1"
                                {...listeners}
                              >
                                <img src={dots} alt="" className="w-1 h-4" />
                              </div>
                              <div>
                                <RTE
                                  name="editor"
                                  heightValue={400}
                                  value={state.templateContent}
                                  defaultValue={state.templateContent}
                                  editorRef={templateEditorRef}
                                  onFocus={handleFocus}
                                  onBlur={handleBlur}
                                  onChange={(value) =>
                                    updateState({ templateContent: value })
                                  }
                                  language={state.language}
                                />
                              </div>
                              <div className="p-4">
                                <div className="text-sm text-gray-900 dark:text-gray-100 mb-2">
                                  <h1 className="font-bold">
                                    {t("templateInstructions")}
                                  </h1>
                                </div>
                                <textarea
                                  dir={
                                    state.language === "ar" ||
                                    state.language === "he"
                                      ? "rtl"
                                      : "ltr"
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#CBEA7B] focus:border-[#CBEA7B] dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 resize-y min-h-[100px]"
                                  value={state.templateInstructions}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 10000) {
                                      updateState({
                                        templateInstructions: value,
                                      });
                                    }
                                  }}
                                  maxLength={10000}
                                  placeholder={t("enterTemplateInstructions")}
                                  rows={4}
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    textAlign:
                                      state.language === "ar" ||
                                      state.language === "he"
                                        ? "right"
                                        : "left",
                                  }}
                                />
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                                  {state.templateInstructions.length}/10,000{" "}
                                  {t("characters")}
                                </div>
                              </div>
                            </div>
                          )}
                          {state.selectedCard === 2 && (
                            <div className="relative bg-white pt-12 rounded-2xl dark:bg-gray-800">
                              <div
                                className="drag-handle bg-[#CBEA7B] hover:bg-[#A4C639] dark:bg-[#A4C639] dark:hover:bg-[#CBEA7B] w-8 h-8 rounded-full flex justify-center items-center absolute z-50 right-0 top-0 mt-1 mr-1"
                                {...listeners}
                              >
                                <img src={dots} alt="" className="w-1 h-4" />
                              </div>
                              <div>
                                <RTE
                                  name="editor"
                                  heightValue={400}
                                  defaultValue={state.clientHeaderContent}
                                  value={state.clientHeaderContent}
                                  editorRef={headerEditorRef}
                                  onFocus={handleFocus}
                                  onBlur={handleBlur}
                                  onChange={(value) =>
                                    debouncedSetClientHeader(value)
                                  }
                                  language={state.language}
                                />
                              </div>
                            </div>
                          )}
                          {state.selectedCard === 3 && (
                            <div className="relative bg-white pt-12 rounded-2xl dark:bg-gray-800">
                              <div
                                className="drag-handle bg-[#CBEA7B] w-8 h-8 rounded-full flex justify-center items-center absolute z-50 right-0 top-0 mt-1 mr-1"
                                {...listeners}
                              >
                                <img src={dots} alt="" className="w-1 h-4" />
                              </div>
                              <div>
                                <RTE
                                  name="editor"
                                  heightValue={400}
                                  defaultValue={state.clientFooterContent}
                                  value={state.clientFooterContent}
                                  editorRef={footerEditorRef}
                                  onFocus={handleFocus}
                                  onBlur={handleBlur}
                                  onChange={(value) =>
                                    debouncedSetClientFooter(value)
                                  }
                                  language={state.language}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </SortableItem>
                  </div>
                );
              } else if (id === "sidebar") {
                return (
                  <div className="lg:w-2/6 w-full" key={id}>
                    <SortableItem id={id}>
                      {(listeners) => (
                        <div>
                          <Sidebar
                            name={state.name}
                            setName={handleSetName}
                            saveTemplate={
                              state.isEditing
                                ? updateExistingTemplate
                                : saveTemplate
                            }
                            macroData={generateMacroData()}
                            isMacroEditing={state.isMacroEditing}
                            isEditing={state.isEditing}
                            selectedCard={state.selectedCard}
                            catalogTableItems={state.catalogTableItems}
                            catalogTableName={state.catalogTableName}
                            macroContent={state.macroContent}
                            templateContent={state.templateContent}
                            onDoubleClickItem={onDoubleClickItem}
                            headerContent={state.headerContent}
                            footerContent={state.footerContent}
                            fetchMacros={refetchMacrosQuery}
                            fetchClientHeader={fetchClientHeader}
                            fetchClientFooter={fetchClientFooter}
                            addMacroAtCursor={addMacroAtCursor}
                            macroEditorRef={macroEditorRef}
                            addTemplateAtCursor={addTemplateAtCursor}
                            templateEditorRef={templateEditorRef}
                            addHeaderAtCursor={addHeaderAtCursor}
                            headerEditorRef={headerEditorRef}
                            addFooterAtCursor={addFooterAtCursor}
                            footerEditorRef={footerEditorRef}
                            language={state.language}
                            anathomy={state.anathomy}
                            setAnathomy={(value) =>
                              updateState({ anathomy: value })
                            }
                            anathomies={state.anathomies}
                            templateSettings={state.templateSettings}
                            updateTemplateSettings={updateTemplateSettings}
                            canManageDefaultTemplates={
                              canManageDefaultTemplates
                            }
                            canCreateOrEditTemplates={canCreateOrEditTemplates}
                            onMacroCreateSuccess={resetEditorDraftState}
                            onResetForm={resetEditorDraftState}
                          />
                        </div>
                      )}
                    </SortableItem>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </SortableContext>
      </DndContext>
      <Footer />
    </div>
  );
}

export default Home;
